import { useEffect, useState, type ReactNode } from "react";
import { NavLink, useLocation } from "react-router";
import { useApi } from "../lib/api.ts";
import { duration } from "../lib/format.ts";
import { useLiveConnected } from "../lib/live.ts";
import type { Overview } from "../lib/types.ts";
import { ErrorBoundary, useNow } from "./ui.tsx";

const BOT_LABEL = { disabled: "Bot off", connecting: "Bot connecting", ready: "Bot online", error: "Bot error" } as const;
const BOT_TONE = { disabled: "", connecting: "warn", ready: "ok", error: "bad" } as const;

/** Sidebar + top status bar around every signed-in page. */
export function Layout({ children }: { children: ReactNode }) {
  const overview = useApi<Overview>("/api/overview", { refreshInterval: 30_000 });
  const connected = useLiveConnected();
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const now = useNow(30_000);
  useEffect(() => {
    setOpen(false);
    window.scrollTo({ top: 0 });
  }, [location.pathname]);

  const o = overview.data;
  const waiting = o?.pending.length ?? 0;
  const rl = o?.rateLimit?.payload;
  const resetsIn = rl?.resetsAt ? rl.resetsAt * (rl.resetsAt < 1e12 ? 1000 : 1) - now : undefined;

  return (
    <div className="shell">
      {open && <div className="scrim" onClick={() => setOpen(false)} />}
      <aside className={`sidebar ${open ? "open" : ""}`}>
        <NavLink to="/" className="brand">
          <img src="/favicon.svg" alt="" /> Switchboard
        </NavLink>
        <NavLink to="/" end className="nav">
          Overview {waiting > 0 && <span className="badge" title="Waiting on you">{waiting}</span>}
        </NavLink>
        <NavLink to="/projects" className="nav">
          Projects
        </NavLink>
        <NavLink to="/sessions" className="nav">
          Sessions
        </NavLink>
        <NavLink to="/bugs" className="nav">
          Bug sheets
        </NavLink>
        <NavLink to="/activity" className="nav">
          Activity
        </NavLink>
        <NavLink to="/usage" className="nav">
          Usage
        </NavLink>
        <div className="navlabel">Settings</div>
        <NavLink to="/settings/discord" className="nav sub">
          Discord
        </NavLink>
        <NavLink to="/settings/claude" className="nav sub">
          Claude
        </NavLink>
        <NavLink to="/settings/server" className="nav sub">
          Server
        </NavLink>
        <NavLink to="/settings/account" className="nav sub">
          Account
        </NavLink>
        <div className="sidefoot">
          <span className="row nowrap">
            <i className={`dot ${connected ? "ok" : "warn"}`} /> {connected ? "Live updates on" : "Reconnecting…"}
          </span>
        </div>
      </aside>
      <div className="main">
        <div className="topbar">
          <button className="btn ghost icon menu" aria-label="Menu" onClick={() => setOpen(true)}>
            ☰
          </button>
          {o && (
            <>
              <NavLink to="/settings/discord" className="row nowrap" style={{ color: "inherit" }} title={o.bot.error}>
                <i className={`dot ${BOT_TONE[o.bot.state]}`} /> {BOT_LABEL[o.bot.state]}
                {o.bot.state === "ready" && o.bot.guild ? <span className="muted">· {o.bot.guild.name}</span> : null}
              </NavLink>
              <span className="muted">
                {o.slots.used} of {o.slots.max} slots used
              </span>
              {rl?.utilization !== undefined && (
                <span className="muted">
                  Max window {Math.round(rl.utilization * (rl.utilization <= 1 ? 100 : 1))}%
                  {resetsIn !== undefined && resetsIn > 0 ? ` · resets in ${duration(resetsIn)}` : ""}
                </span>
              )}
              {waiting > 0 && (
                <NavLink to="/" className="pill warn">
                  {waiting} waiting on you
                </NavLink>
              )}
            </>
          )}
        </div>
        <main className="content">
          <ErrorBoundary resetKey={location.pathname}>{children}</ErrorBoundary>
        </main>
      </div>
    </div>
  );
}
